import { withForwardedEnv } from '../config/forwardedEnv';
import type { ConsentToken } from './context';
import {
  HttpError,
  InvalidContextToken,
  NoScopesError,
  UnauthorizedScopesError,
  UnexpectedError,
} from './errors';

const CONSENT_INFO_URL = '/consent/info';

export async function getConsentInfo(
  consent: ConsentToken,
  options?: { selectedOrg?: string; includeDisabledScopes?: boolean },
): Promise<ConsentInfo | ConsentInfoError> {
  const params: Record<string, string> = { context: consent.raw };
  if (options?.selectedOrg) {
    params.selectedOrg = options.selectedOrg;
  }
  if (options?.includeDisabledScopes) {
    params.includeDisabledScopes = 'true';
  }
  const response = await fetch(
    withForwardedEnv(CONSENT_INFO_URL + '?' + new URLSearchParams(params)),
  );
  switch (response.status) {
    case 200:
      console.log('Received consent info');
      return parseSuccessBody(response);
    case 400:
    case 401:
      const error = await response.json();
      console.warn('Error verifying context', error);
      return resolveError(response.status, consent, error);
    default:
      console.error(`Unexpected status code [${response.status}]:`, response);
      throw new HttpError(
        response.status,
        `Unexpected status code [${response.status}] when calling /consent/info`,
      );
  }
}

export type AvailableSite = {
  ari: string;
  url: string;
  avatar?: string;
  name?: string;
};

export type ScopeThumbprint = {
  thumbprint: string;
  scopes: string[];
};

/** A Bitbucket workspace available for MCP consent selection (from /consent/info). */
export type BitbucketWorkspace = {
  /** Bitbucket workspace ARI (e.g. "ari:cloud:bitbucket::workspace/<uuid>"). */
  ari: string;
  /** Display name of the workspace. */
  name: string;
  /** Workspace avatar URL, when set. */
  avatar?: string;
  /** Atlassian organization ID that owns the workspace, when available. */
  orgId?: string;
};

export type ForgeMcpConsentTarget = {
  /** Stable Forge app identifier used as the consent target. */
  appId: string;
  /** Forge app display name resolved by the server. */
  appName: string;
  /** Forge app avatar URL resolved by the server, when set. */
  appIcon?: string;
  /** Forge app developer's name from its Marketplace listing, when listed. */
  vendorName?: string;
  /** Forge app developer's privacy policy URL from its Marketplace listing, when listed. */
  privacyPolicy?: string;
};

export function getForgeMcpAppName(
  info: Pick<ConsentInfo, 'forgeMcpConsentTarget'> | undefined,
): string | undefined {
  const appName = info?.forgeMcpConsentTarget?.appName?.trim();
  return appName || undefined;
}

export function getForgeMcpAppIcon(
  info: Pick<ConsentInfo, 'forgeMcpConsentTarget'> | undefined,
): string | undefined {
  const appIcon = info?.forgeMcpConsentTarget?.appIcon?.trim();
  return appIcon || undefined;
}

export type ConsentInfo = {
  /** Forge app targeted by an MCP consent. Absent for standard MCP flows. */
  forgeMcpConsentTarget?: ForgeMcpConsentTarget;
  siteName?: string;
  client?: ClientConsentInfo;
  availableWorkspaces?: string[];
  siteMapping?: Record<string, string>;
  availableSites?: AvailableSite[];
  filteredScopes?: string[];
  disabledScopes?: string[];
  availableOrganizations?: OrgInfo[];
  selectedOrgInfo?: OrgInfo;
  /** Whether the user has Trello product access (returned by /consent/info for MCP flows). */
  hasTrelloProductAccess?: boolean;
  /** Bitbucket workspaces the user can grant access to (MCP flows, gated). */
  availableBitbucketWorkspaces?: BitbucketWorkspace[];
  /**
   * Maximum number of Trello workspaces the user may grant access to in one
   * consent. Absent means the selection is unbounded.
   */
  maxTrelloWorkspaces?: number;
  scopeThumbprint?: ScopeThumbprint;
};

export type ScopeInfo = {
  scopeDescription: string;
  name: string;
  groupName?: string;
};

export type ClientConsentInfo = {
  clientId: string;
  name: string;
  profileName?: string;
  appId?: string;
  appLogoUrl?: string;
  appVendorName?: string;
  createdAt?: number;
  appPrivacyPolicyLink?: string;
  lastGdprPollTime?: number;
  appStoresUserData?: boolean;
  appDescription?: string;
  appContactLink?: string;
  appEulaLink?: string;
  callbacks?: string[];
  scopes?: ScopeInfo[];
};

/**
 * A product workspace inside an organization unit, as `/consent/info` returns
 * it under `availableOrganizations[].units[].workspaces[]`.
 *
 * `cloudUrl` and `workspaceUrl` carry a scheme (`https://pug.jira-dev.com`),
 * unlike the bare hosts in `availableWorkspaces` and `availableSites[].url`.
 */
export type OrgUnitWorkspace = {
  cloudId: string;
  cloudUrl: string;
  /** Product-scoped workspace ARI, e.g. `ari:cloud:confluence:<cloudId>:workspace/<uuid>`. */
  workspaceAri: string;
  workspaceAvatarUrl?: string;
  workspaceUrl: string;
};

/**
 * A collaboration-context unit grouping workspaces within an organization
 * (`ari:cloud:platform::collaboration-context/<uuid>`).
 */
export type OrgUnit = {
  ari: string;
  workspaces?: OrgUnitWorkspace[];
};

export type OrgInfo = {
  orgId: string;
  name?: string;
  /** Bare site hosts belonging to this org, e.g. `pug.jira-dev.com`. */
  availableWorkspaces?: string[];
  /** Collaboration-context units, each carrying the org's product workspaces. */
  units?: OrgUnit[];
};

async function parseSuccessBody(response: Response): Promise<ConsentInfo> {
  const body = await response.json();
  if (typeof body !== 'object') {
    console.error('Success body is not an object', body);
    throw Error('Unexpected body in consent info call', { cause: response });
  }
  if (body.site_name) {
    body.siteName = body.site_name;
    delete body.site_name;
  }
  if (body.has_trello_product_access != null) {
    body.hasTrelloProductAccess = body.has_trello_product_access;
    delete body.has_trello_product_access;
  }
  if (body.max_trello_workspaces != null) {
    body.maxTrelloWorkspaces = body.max_trello_workspaces;
    delete body.max_trello_workspaces;
  }
  return body;
}

export type ConsentInfoError =
  UnauthorizedScopesError | NoScopesError | UnexpectedError;

function resolveError(
  status: number,
  consent: ConsentToken,
  errorObject: Record<string, string>,
): ConsentInfoError {
  switch (errorObject.error) {
    case 'unauthorized_scopes':
      return {
        error: 'unauthorized-scopes',
        clientName: consent.clientName ?? '',
        products: consent.customer === 'trello' ? ['Trello'] : [],
        scopes: errorObject.error_description.split('+'),
      };
    case 'no_scopes':
      return {
        error: 'no-scopes',
        clientName: consent.clientName ?? '',
      };
    case 'invalid_request':
    case 'unauthorized_user':
      return InvalidContextToken;
    default:
      throw new HttpError(status, 'Unexpected error in consent info call', {
        cause: errorObject,
      });
  }
}
